import type { Expr, Program, Stmt } from '../ir.js';
import { stmtExprs, walkExpr } from '../ir.js';
import { CodeWriter } from '../writer.js';
import type { Code, GenContext } from './context.js';
import {
  ATOM,
  joinParts,
  MISSING,
  numberLiteral,
  sliceEnd,
  stringLiteral,
  wrap,
} from './context.js';

/**
 * Where each variable is declared in the JavaScript view:
 * - `let x = …` at its first use when that is a top-level assignment that doesn't read `x`;
 * - `for (let i = …` when the variable only ever appears inside for-loops counting with it;
 * - otherwise hoisted into one `let a, b;` line at the top.
 */
function declarations(p: Program) {
  const first = new Map<string, { stmt: Stmt; top: boolean }>();
  const unbound = new Set<string>();
  const refs = (s: Stmt): Set<string> => {
    const names = new Set<string>();
    if (s.k === 'set' || s.k === 'change' || s.k === 'for') names.add(s.name);
    for (const e of stmtExprs(s)) for (const x of walkExpr(e)) if (x.k === 'var') names.add(x.name);
    return names;
  };
  const visit = (body: readonly Stmt[], loops: readonly string[], top: boolean) => {
    for (const s of body) {
      for (const n of refs(s)) {
        if (!first.has(n)) first.set(n, { stmt: s, top });
        const boundHere =
          s.k === 'for' && s.name === n && !stmtExprs(s).some((e) => mentions(e, n));
        if (!boundHere && !loops.includes(n)) unbound.add(n);
      }
      switch (s.k) {
        case 'if':
          for (const b of s.branches) visit(b.body, loops, false);
          if (s.orElse) visit(s.orElse, loops, false);
          break;
        case 'repeat':
        case 'while':
          visit(s.body, loops, false);
          break;
        case 'for':
          visit(s.body, [...loops, s.name], false);
          break;
        default:
          break;
      }
    }
  };
  visit(p.body, [], true);

  const inlineSet = new Set<string>();
  const forLet = new Set<string>();
  const hoisted: string[] = [];
  for (const name of p.variables) {
    const f = first.get(name);
    if (f && f.top && f.stmt.k === 'set' && f.stmt.name === name && !mentions(f.stmt.value, name)) {
      inlineSet.add(f.stmt.id);
    } else if (!unbound.has(name)) {
      forLet.add(name);
    } else {
      hoisted.push(name);
    }
  }
  return { inlineSet, forLet, hoisted };
}

function mentions(e: Expr, name: string): boolean {
  for (const x of walkExpr(e)) if (x.k === 'var' && x.name === name) return true;
  return false;
}

// Precedence, loosely following JavaScript's grammar.
const JS_ZERO: Record<string, string> = {
  int: '0',
  double: '0',
  string: "''",
  bool: 'false',
};

const P = { or: 1, and: 2, eq: 3, rel: 4, add: 5, mul: 6, unary: 7 };

function templatePart(s: string): string {
  return s.replace(/\\/g, '\\\\').replace(/`/g, '\\`').replace(/\$\{/g, '\\${');
}

export function generateJs(ctx: GenContext): CodeWriter {
  const w = new CodeWriter('  ');
  const decl = declarations(ctx.program);

  const expr = (e: Expr): Code => {
    switch (e.k) {
      case 'num':
        return { code: numberLiteral(e.value), prec: e.value < 0 ? P.unary : ATOM };
      case 'text':
        return { code: stringLiteral(e.value), prec: ATOM };
      case 'bool':
        return { code: String(e.value), prec: ATOM };
      case 'var':
        return { code: ctx.id(e.name), prec: ATOM };
      case 'read':
        return {
          code:
            e.as === 'int' ? 'readNumber()' : e.as === 'double' ? 'readDecimal()' : 'readText()',
          prec: ATOM,
        };
      case 'arith': {
        if (e.op === 'idiv') {
          return {
            code: `Math.trunc(${wrap(expr(e.left), P.mul)} / ${wrap(expr(e.right), P.mul + 1)})`,
            prec: ATOM,
          };
        }
        const op = e.op === 'mod' ? '%' : e.op;
        const prec = op === '+' || op === '-' ? P.add : P.mul;
        return {
          code: `${wrap(expr(e.left), prec)} ${op} ${wrap(expr(e.right), prec + 1)}`,
          prec,
        };
      }
      case 'compare': {
        const op = e.op === '==' ? '===' : e.op === '!=' ? '!==' : e.op;
        const prec = e.op === '==' || e.op === '!=' ? P.eq : P.rel;
        return {
          code: `${wrap(expr(e.left), P.add)} ${op} ${wrap(expr(e.right), P.add)}`,
          prec,
        };
      }
      case 'logic': {
        const prec = e.op === 'and' ? P.and : P.or;
        const op = e.op === 'and' ? '&&' : '||';
        return {
          // Nested and/or always get parentheses: clearer, and g++ -Wall asks for them.
          code: `${wrap(expr(e.left), P.and + 1)} ${op} ${wrap(expr(e.right), P.and + 1)}`,
          prec,
        };
      }
      case 'not':
        return { code: `!${wrap(expr(e.arg), P.unary)}`, prec: P.unary };
      case 'join': {
        const body = joinParts(e)
          .map((part) => (part.k === 'text' ? templatePart(part.value) : `\${${expr(part).code}}`))
          .join('');
        return { code: `\`${body}\``, prec: ATOM };
      }
      case 'length':
        return { code: `${wrap(expr(e.arg), ATOM)}.length`, prec: ATOM };
      case 'code':
        return { code: `${wrap(expr(e.arg), ATOM)}.charCodeAt(0)`, prec: ATOM };
      case 'chr':
        return { code: `String.fromCharCode(${expr(e.arg).code})`, prec: ATOM };
      case 'case':
        return {
          code: `${wrap(expr(e.arg), ATOM)}.${e.upper ? 'toUpperCase' : 'toLowerCase'}()`,
          prec: ATOM,
        };
      case 'find':
        return { code: `${wrap(expr(e.hay), ATOM)}.indexOf(${expr(e.needle).code})`, prec: ATOM };
      case 'substr': {
        const text = wrap(expr(e.arg), ATOM);
        const end =
          e.from.k === 'num' && e.from.value === 0
            ? expr(e.len).code
            : sliceEnd(e.from, e.len, expr, P.add);
        return { code: `${text}.slice(${expr(e.from).code}, ${end})`, prec: ATOM };
      }
      case 'at': {
        const idx = `[${expr(e.index).code}]${e.index2 ? `[${expr(e.index2).code}]` : ''}`;
        return { code: `${ctx.id(e.name)}${idx}`, prec: ATOM };
      }
      case 'size':
        return {
          code: e.dim === 2 ? `${ctx.id(e.name)}[0].length` : `${ctx.id(e.name)}.length`,
          prec: ATOM,
        };
      case 'missing':
        return { code: MISSING, prec: ATOM };
    }
  };

  const e = (x: Expr) => expr(x).code;

  const block = (body: readonly Stmt[]) => w.indented(() => body.forEach(stmt));

  const stmt = (s: Stmt): void => {
    switch (s.k) {
      case 'set': {
        const kw = decl.inlineSet.has(s.id) ? 'let ' : '';
        w.stmt(s, () => w.line(`${kw}${ctx.id(s.name)} = ${e(s.value)};`));
        break;
      }
      case 'change': {
        const name = ctx.id(s.name);
        const d = s.delta;
        const text =
          d.k === 'num' && d.value === 1
            ? `${name}++;`
            : d.k === 'num' && d.value === -1
              ? `${name}--;`
              : d.k === 'num' && d.value < 0
                ? `${name} -= ${numberLiteral(-d.value)};`
                : `${name} += ${e(d)};`;
        w.stmt(s, () => w.line(text));
        break;
      }
      case 'print':
        w.stmt(s, () => {
          if (s.end === 'newline') w.line(`print(${e(s.value)});`);
          else if (s.end === 'space') w.line(`write(${e(s.value)}, " ");`);
          else w.line(`write(${e(s.value)});`);
        });
        break;
      case 'if':
        w.stmt(
          s,
          () => w.line(`if (${e(s.branches[0]!.cond)}) {`),
          () => {
            s.branches.forEach((b, i) => {
              if (i > 0) w.mapExpr(b.cond, w.line(`} else if (${e(b.cond)}) {`));
              block(b.body);
            });
            if (s.orElse) {
              w.line('} else {');
              block(s.orElse);
            }
            w.line('}');
          },
        );
        break;
      case 'repeat': {
        const k = ctx.counter(s.id);
        w.stmt(
          s,
          () => w.line(`for (let ${k} = 1; ${k} <= ${wrap(expr(s.times), P.add)}; ${k}++) {`),
          () => {
            block(s.body);
            w.line('}');
          },
        );
        break;
      }
      case 'while':
        w.stmt(
          s,
          () => w.line(`while (${e(s.cond)}) {`),
          () => {
            block(s.body);
            w.line('}');
          },
        );
        break;
      case 'for': {
        const i = ctx.id(s.name);
        const kw = decl.forLet.has(s.name) ? 'let ' : '';
        const [cmp, step] = s.down ? ['>=', '--'] : ['<=', '++'];
        w.stmt(
          s,
          () =>
            w.line(
              `for (${kw}${i} = ${e(s.from)}; ${i} ${cmp} ${wrap(expr(s.to), P.add)}; ${i}${step}) {`,
            ),
          () => {
            block(s.body);
            w.line('}');
          },
        );
        break;
      }
      case 'newArray': {
        const zero = JS_ZERO[s.elem] ?? '0';
        // One extra place when the list is numbered from 1, so `a[n]` exists.
        const size = (d: Expr) => (s.base === 1 ? `${wrap(expr(d), P.add)} + 1` : e(d));
        const [rows, cols] = s.dims;
        const make = cols
          ? `Array.from({ length: ${size(rows)} }, () => new Array(${size(cols)}).fill(${zero}))`
          : `new Array(${size(rows)}).fill(${zero})`;
        const name = ctx.id(s.name);
        w.stmt(s, () => w.line(`${decl.inlineSet.has(s.id) ? 'let ' : ''}${name} = ${make};`));
        break;
      }
      case 'setAt': {
        const idx = `[${e(s.index)}]${s.index2 ? `[${e(s.index2)}]` : ''}`;
        w.stmt(s, () => w.line(`${ctx.id(s.name)}${idx} = ${e(s.value)};`));
        break;
      }
      case 'break':
        w.stmt(s, () => w.line('break;'));
        break;
    }
  };

  if (decl.hoisted.length > 0) w.line(`let ${decl.hoisted.map(ctx.id).join(', ')};`);
  ctx.program.body.forEach(stmt);
  w.mapAll(ctx.program.startId);
  return w;
}
